import React, { useState } from 'react'

import { useTranslation } from 'react-i18n-lite'

import { useDocumentTitle } from 'infrastructure/shared/hooks'

import {
  useAuthentication,
  useRedirectOrigin
} from 'authentication/shared/hooks'

import {
  Button,
  feedbackcard,
  SkeletonDots,
  StandardUniselection
} from '@yes.technology/react-toolkit'

import SuggestionChangePassword from 'authentication/Login/LoginContainer/SuggestionChangePassword'

import AuthenticationTemplate from 'authentication/shared/components/AuthenticationTemplate'

import usePasswordChangeSuggestion from 'authentication/shared/hooks/usePasswordChangeSuggestion'

import { CredentialOptions } from 'authentication/shared/types'

import Title from 'shared/components/Title'

import useCredentials from './useCredentials'

const STATIC_ECOSYSTEM_OPTION = {
  id: 'Yes Ecosystem',
  value: 'Yes Ecosystem',
  label: 'Yes Ecosystem'
}

const Credentials = () => {
  const { t } = useTranslation()

  const [shouldDisableSubmit, setShouldDisableSubmit] = useState(false)

  const { redirectToOrigin } = useRedirectOrigin()

  const { authenticatedData } = useAuthentication()

  const { shouldOpenPasswordChange, handleSuggestPasswordChange } =
    usePasswordChangeSuggestion()

  const {
    credentials,
    submitCredentials,
    selectedCredentials,
    isCredentialsValid,
    isLoading: isFetchingCredentials,
    setEcosystem,
    setApplicationSystem,
    setOrganization,
    setUser
  } = useCredentials()

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    setShouldDisableSubmit(true)

    const onSuccess = async (upgradedToken: string) => {
      feedbackcard(t('credentials.feedback.success.title'), {
        message: t('credentials.feedback.success.msg'),
        type: 'success'
      })

      const isRedirectToOrigin = !handleSuggestPasswordChange(authenticatedData)

      if (isRedirectToOrigin) {
        redirectToOrigin(upgradedToken)
      } else {
        setShouldDisableSubmit(false)
      }
    }

    const onError = () => setShouldDisableSubmit(false)

    await submitCredentials(onSuccess, onError)
  }

  useDocumentTitle({ title: t('credentials.title') })

  return (
    <AuthenticationTemplate>
      <Title>{t('credentials.title')}</Title>

      {isFetchingCredentials && <SkeletonDots />}

      {!isFetchingCredentials && credentials && (
        <form
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          onSubmit={handleSubmit}
        >
          <StandardUniselection
            label={t('active-session.ecosystem-credentials')}
            options={[STATIC_ECOSYSTEM_OPTION]}
            selectedOption={STATIC_ECOSYSTEM_OPTION}
            onSelect={() => {
              setEcosystem(STATIC_ECOSYSTEM_OPTION.id)
            }}
            heightClass='regular'
            required
          />
          <StandardUniselection
            label={t('active-session.applicationsystem-credential')}
            placeholder={t('credentials.select-application-system')}
            options={credentials.applicationSystem.options}
            selectedOption={credentials.applicationSystem?.options.find(
              (option) =>
                option.id === selectedCredentials.applicationsystem_uuid
            )}
            onSelect={(selected) => {
              setApplicationSystem((selected as CredentialOptions)?.id)
            }}
            heightClass='regular'
            required
          />
          <StandardUniselection
            label={t('active-session.organization-credential')}
            placeholder={t('credentials.select-organization-credential')}
            options={credentials.organization.options}
            selectedOption={credentials.organization?.options.find(
              (option) => option.id === selectedCredentials.organization_uuid
            )}
            onSelect={(selected) => {
              setOrganization((selected as CredentialOptions)?.id)
            }}
            heightClass='regular'
            required
          />
          <StandardUniselection
            label={t('active-session.user-credential')}
            placeholder={t('credentials.select-user-credential')}
            options={credentials.user.options}
            selectedOption={credentials.user?.options.find(
              (option) => option.id === selectedCredentials.user_uuid
            )}
            onSelect={(selected) => {
              setUser((selected as CredentialOptions)?.id)
            }}
            heightClass='regular'
            required
          />
          <Button
            disabled={shouldDisableSubmit || !isCredentialsValid}
            impactClass='workflow'
            type='submit'
          >
            {t('credentials.apply')}
          </Button>
        </form>
      )}
      <SuggestionChangePassword isOpen={shouldOpenPasswordChange} />
    </AuthenticationTemplate>
  )
}

export default Credentials
